import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {finalize, map, catchError} from 'rxjs/operators';

import {CITY2, isCity2} from "../../defines/gukeo.const";
import {CITY2_INFO_LIST} from '../../defines/desktop.const';
import {VIDEO_API_URL, VIDEO_CITY_API_URL, VIDEO_STORY_API_URL} from '../../defines/protocol';
import {SearchParam} from '../../defines/client.const';
import {NormalizeSearchKeyword} from '../../defines/client.func';
import {Video, VideoStory} from '../../models/video.model';
import {VideoLoader} from '../../interfaces/VideoLoader';
import {ApiService} from '../api/api.service';


/**
 * 비디오는 아직 양이 많지 않아서 Pagenation을 사용하지 않는다.
 * TODO: 비디오의 양이 많아지면, 클라이언트의 화면 표시 정책을 정한 후 프로토콜도 수정해야 함.
 */
@Injectable({
  providedIn: 'root'
})
export class VideoService implements VideoLoader {
  protected urlApi = VIDEO_API_URL;

  private load$: Observable<Array<Video>> | null = null;

  public IsLoading(): boolean {
    return this.load$ !== null;
  }


  constructor(
    protected api: ApiService,
  ) { }

  public GetList(param?: SearchParam): Observable<Array<Video>> {
    if (this.load$) {
      return this.load$;
    }

    let url = this.urlApi;
    if (param) {
      const searchParams = new URLSearchParams();

      // 우선순위: id가 있으면 id, 없으면 keyword를 사용
      if (param.id) {
        searchParams.set('id', param.id);
      } else if (param.keyword) {
        const keyword = NormalizeSearchKeyword(param.keyword);
        if (keyword) {
          searchParams.set('search', keyword);
        }
      }

      // story 파라미터 설정 (숫자형일 경우 toString 처리)
      if (param.story !== undefined && param.story !== null) {
        searchParams.set('story', param.story.toString());
      }

      // city 파라미터: 기존 파라미터가 있으면 'region', 없으면 'city'로 지정
      if (param.city) {
        if (searchParams.toString()) {
          searchParams.set('region', param.city);
        } else {
          searchParams.set('city', param.city);
        }
      }

      // limit 값이 0보다 크면 추가
      if (param.limit && param.limit > 0) {
        searchParams.set('limit', param.limit.toString());
      }

      // category 파라미터가 있다면 추가 (enum 또는 숫자일 경우 toString 처리)
      if ('cat' in param && param.cat) {
        searchParams.set('cat', param.cat.toString());
      }

      const queryString = searchParams.toString();
      if (queryString) {
        url = `${this.urlApi}?${queryString}`;
      }
    }

    console.log('VideoService::GetList() url =', url);

    return this.load$ = this.api.Get(url).pipe(
      finalize(() => {
        this.load$ = null;
      }),
      map((data: any[]) => {
        console.log('VideoService::GetList() param, data =', param, data);
        const result = new Array<Video>();
        data.forEach(value => result.push(new Video(value)));
        return result;
      })
    );
  }


  /**************************************************************************************************
   * Video Story 목록 받아오기
   **************************************************************************************************/
  public GetStoryList(): Observable<Array<VideoStory>>
  {
    return this.api.Get(VIDEO_STORY_API_URL).pipe(
      map((data: unknown) => {
        if (!Array.isArray(data)) {
          throw new Error('응답 데이터 형식이 올바르지 않습니다. 배열이 필요합니다.');
        }
        const result = new Array<VideoStory>();
        data.forEach(value => result.push(new VideoStory(value)));

        console.log('VideoService::GetStoryList() result =', result, data);
        return result;
      }),
      catchError(error => {
        console.error('GetStoryList 함수 처리 중 오류 발생:', error);
        // 오류 발생 시 빈 배열 반환
        return of([] as Array<VideoStory>);
      })
    );
  }

  /**
   * API로부터 받아온 CITY2 값을 기반으로, 해당하는 CITY2 목록을 반환한다.
   * 항상 '전국'은 포함한다.
   */
  public GetCityList(): Observable<CITY2[]> {
    return this.api.Get(VIDEO_CITY_API_URL).pipe(
      map((data: unknown) => {
        if (!Array.isArray(data)) {
          throw new Error('응답 데이터 형식이 올바르지 않습니다. 배열이 필요합니다.');
        }

        const cities: CITY2[] = [];
        data.forEach((item: any) => {
          // item이 문자열 형태라면 CITY2 타입으로 간주
          if (isCity2(item)) {
            cities.push(item);
          } else {
            console.warn('유효하지 않은 도시 아이템:', item);
          }
        });
        // '전국'은 항상 포함
        if (!cities.includes('전국')) {
          cities.push('전국');
        }
        // 정적 CITY2_INFO_LIST에서 API에서 반환된 도시(CITY2)에 해당하는 항목들의 city2 값을 추출하여 반환
        return CITY2_INFO_LIST
          .filter(info => cities.includes(info.city2))
          .map(info => info.city2);
      }),
      catchError(error => {
        console.error('GetCityList 함수 처리 중 오류 발생:', error);
        // 오류 발생 시 빈 배열 반환
        return of([] as CITY2[]);
      })
    );
  }
}
